import { bookingType } from 'Models/Enums';
import React, { useEffect, useMemo } from 'react';
import { BookingStatusTagOption } from 'Views/Components/_HumanWritten/Bookings/BookingStatusTag';
import StaffBookingFilters from 'Views/Components/_HumanWritten/StaffTables/StaffBookings/StaffBookingFilters';
import { StaffBookingsCollection } from 'Views/Components/_HumanWritten/StaffTables/StaffBookings/StaffBookingsCollection';
import If from 'Views/Components/If/If';
import { isNotNullOrUndefined } from 'Util/TypeGuards';
import { FetchBookingsCount, FetchBookingsWithFilters } from 'Util/_HumanWritten/Bookings/FetchBookingsWithFilters';
import { BookingOrderByOption } from 'Util/_HumanWritten/Bookings/FetchBookingsUtils';
import { STAFF_TABLE_SECTIONS, useResetFiltersOnSectionEntry } from 'Util/_HumanWritten/Navigation/staffTableSections';
import classNames from 'classnames';
import {
	clearStashedMainBookingFilters,
	restoreStashedMainBookingFilters,
	stashMainBookingFilters,
	useStaffBookingsStore,
} from './StaffBookingsFilterStore';
import { useDebounce } from 'Hooks/useDebounce';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useDynamicBookingFieldsStore } from 'Stores/DynamicBookingFieldsStore';

export interface StaffBookingsComponentProps {
	userId?: string;
	resultsPerPage?: number;
	onResultsPage?: boolean;
	searchResultsPage?: boolean;
}

export interface StaffBookingsFilters {
	startDate?: Date;
	endDate?: Date;
	filterByCreatedDate: boolean;
	bookingFilterType: string;
	bookingTypes: bookingType[];
	routeFilters: string[];
	orderBy: BookingOrderByOption;
	orderByDescending: boolean;
	pageNo: number;
	statusFilters: BookingStatusTagOption[];
}

export function StaffBookingsListOuter({ userId, resultsPerPage, onResultsPage }: StaffBookingsComponentProps) {
	const perPage = isNotNullOrUndefined(resultsPerPage) ? resultsPerPage : 10;
	const MINIMUM_LOADER_TIME = 300;

	// Must stay above the store subscription: it resets during render so the first query key is
	// already built from the defaults. `onResultsPage` keeps its old behaviour of always forcing
	// the user-bookings defaults on mount — but because that embedded copy shares this store with
	// the main table, it hands the main table's filters to the stash first so navigating into a
	// customer's account and back keeps them (see StaffBookingsFilterStore).
	const isEmbedded = onResultsPage === true;
	useResetFiltersOnSectionEntry(
		STAFF_TABLE_SECTIONS.BOOKINGS,
		() => {
			if (isEmbedded) {
				stashMainBookingFilters();
			} else {
				clearStashedMainBookingFilters();
			}
			useStaffBookingsStore.getState().resetFilters(isEmbedded);
		},
		isEmbedded,
		restoreStashedMainBookingFilters,
	);

	const { filters, selectedBookingIds, visibleColumns, setSelectedBookingIds, updateFilters, rebuildColumns } =
		useStaffBookingsStore();

	// Load the dynamic booking fields so a column can be offered per field, then
	// rebuild the column list once they arrive (the defaults are computed before
	// the fetch resolves).
	const { loaded: dynamicFieldsLoaded, loadFields } = useDynamicBookingFieldsStore();
	useEffect(() => {
		if (!dynamicFieldsLoaded) loadFields();
	}, [dynamicFieldsLoaded, loadFields]);
	useEffect(() => {
		if (dynamicFieldsLoaded) rebuildColumns();
	}, [dynamicFieldsLoaded, rebuildColumns]);

	const debouncedSearchFilters = useDebounce(
		{
			...filters,
			pageNo: undefined,
		},
		150,
	);

	const queryFilters = useMemo(
		() => ({
			...debouncedSearchFilters,
			pageNo: filters.pageNo,
		}),
		[debouncedSearchFilters, filters.pageNo],
	);

	// Only the visible column keys belong in the query key — that is all the request sends. Keying on
	// the full ColumnConfig[] would mint a new cache entry whenever a label or flag changed, and
	// rebuildColumns() recomputes those from app config on mount once the dynamic fields load.
	const requestedColumns = useMemo(() => visibleColumns.filter(x => x.visible).map(x => x.key), [visibleColumns]);

	// 5. The Query
	const { data, isLoading, isFetching, refetch, dataUpdatedAt } = useQuery({
		queryKey: ['staffBookings', userId, queryFilters, perPage, requestedColumns],
		queryFn: async () => {
			const apiPromise = FetchBookingsWithFilters({
				searchString: queryFilters.searchString,
				bookingFilterType: queryFilters.bookingFilterType,
				bookingTypes: queryFilters.bookingTypes,
				travelStatusFilters: queryFilters.travelStatusFilters,
				routeFilters: queryFilters.routeFilters,
				startDate: queryFilters.startDate,
				endDate: queryFilters.endDate,
				filterByCreatedDate: queryFilters?.filterByCreatedDate ?? false,
				statusFilters: queryFilters.statusFilters,
				orderBy: queryFilters.orderBy,
				orderByDescending: queryFilters.orderByDescending,
				skip: perPage * (queryFilters.pageNo || 0),
				take: perPage,
				userId: userId,
				initialFetch: true,
				columns: requestedColumns,
				skipCount: true,
			});

			const [result] = await Promise.all([
				apiPromise,
				new Promise(resolve => setTimeout(resolve, MINIMUM_LOADER_TIME)),
			]);

			return result;
		},
		placeholderData: keepPreviousData,
		staleTime: 1000 * 60,
	});

	// The total, fetched on its own. The COUNT is most of the list request's cost (it walks every
	// matching booking), so it no longer holds up the rows, and its key leaves out the page and the
	// sort order — neither changes the total — so paging reuses it instead of recounting. Columns
	// matter only through the legacy-id search clause, so only that is keyed. Same 'staffBookings'
	// prefix, so anything invalidating the list invalidates the total too.
	const countFilters = useMemo(() => {
		const { orderBy, orderByDescending, pageNo, ...rest } = debouncedSearchFilters;
		return rest;
	}, [debouncedSearchFilters]);
	const countColumns = requestedColumns.includes('legacyId') ? ['legacyId'] : [];
	const { data: totalCount, refetch: refetchCount } = useQuery({
		queryKey: ['staffBookings', 'count', userId, countFilters, countColumns],
		queryFn: () =>
			FetchBookingsCount({
				searchString: countFilters.searchString,
				bookingFilterType: countFilters.bookingFilterType,
				bookingTypes: countFilters.bookingTypes,
				travelStatusFilters: countFilters.travelStatusFilters,
				routeFilters: countFilters.routeFilters,
				startDate: countFilters.startDate,
				endDate: countFilters.endDate,
				filterByCreatedDate: countFilters?.filterByCreatedDate ?? false,
				statusFilters: countFilters.statusFilters,
				userId: userId,
				initialFetch: true,
				columns: countColumns,
			}),
		staleTime: 1000 * 60,
	});

	// 6. Handlers
	const handleRefresh = () => {
		refetch();
		refetchCount();
	};

	const handleFiltersChange = (newFilters: StaffBookingsFilters) => {
		updateFilters(newFilters);
	};

	// Classname logic from original
	const classes = ['staff-bookings'];
	if (!!userId) {
		classes.push('search-results');
	}

	return (
		<div className={classNames('frontend-admin-page', classes)}>
			{!onResultsPage && (
				<>
					<div className="frontend-admin-page__layout--wide">
						<h1>Bookings</h1>
					</div>
					<div className="page-header__border" />
				</>
			)}

			<StaffBookingFilters setRefresh={handleRefresh} totalBookingCount={totalCount ?? 0} userId={userId} />

			<StaffBookingsCollection
				className="staff__table staff__table-user__bookings"
				bookings={data?.data ?? null}
				totalBookingCount={totalCount}
				perPage={perPage}
				filters={filters}
				setFilters={handleFiltersChange}
				isLoading={isLoading || isFetching}
				selectedBookingIds={selectedBookingIds}
				setSelectedBookingIds={setSelectedBookingIds}
				lastUpdated={dataUpdatedAt ? new Date(dataUpdatedAt) : new Date()}
				refresh={handleRefresh}
				hideCustomerColumn={onResultsPage}
			/>
		</div>
	);
}
